import { deleteEvent } from "@/components/calendars/api.ts";
import { eventPendingDeleteAtom } from "@/components/calendars/eventDeleteAtom.ts";
import { RecurringEditScopeDialog } from "@/components/calendars/RecurringEditScopeDialog.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog.tsx";
import { errorToast } from "@/components/ui/toast.tsx";
import type { CalendarEvent, RecurringEditScope } from "@/types/events.ts";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAtom } from "jotai";
import { Loader2 } from "lucide-react";

/**
 * "Are you sure?" for deleting an event, and the delete itself.
 *
 * Mounted once per screen next to `HeaderUtilities`, not by whatever surface
 * the user pressed Delete on. That surface closes as part of requesting the
 * delete — which is the whole point, since an event's detail panel has nothing
 * useful to say while you decide whether to destroy it — and a confirmation it
 * rendered would close with it. See {@link eventPendingDeleteAtom}.
 *
 * Renders nothing until an event is pending, so the common case costs a single
 * atom read.
 */
export const EventDeleteDialogs = () => {
  const [event, setEvent] = useAtom(eventPendingDeleteAtom);
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: deleteEvent,
    onMutate: async (variables) => {
      if (!event) return;
      const recurringId = event.recurringEventId;
      const editScope = variables.editScope;
      await queryClient.cancelQueries({ queryKey: ["calendarEvents"] });
      queryClient.setQueriesData<{ events: CalendarEvent[] }>(
        { queryKey: ["calendarEvents"] },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            events: old.events.filter((e) => {
              // Always remove the deleted event itself
              if (e.gcalId === event.gcalId) return false;
              if (!recurringId || e.recurringEventId !== recurringId) return true;
              // Remove all siblings for "allEvents"
              if (editScope === "allEvents") return false;
              // Remove future siblings for "thisAndFollowing"
              if (editScope === "thisAndFollowing" && e.start.dateTime >= event.start.dateTime)
                return false;
              return true;
            }),
          };
        },
      );
    },
    onError: () => {
      errorToast("Failed to delete event");
      // Roll back by refetching
      queryClient.invalidateQueries({ queryKey: ["calendarEvents"] });
      queryClient.invalidateQueries({ queryKey: ["source"] });
    },
  });

  if (!event) return null;

  const isOrganizer = event.participants?.find((p) => p.self)?.role === "organizer";
  const dismiss = () => setEvent(null);

  const runDelete = (editScope?: RecurringEditScope) => {
    if (!event.externalEventId || !event.gcalId) return;
    deleteMutation.mutate({
      sourceEntId: event.gcalId,
      calendarId: event.calendarId,
      externalEventId: event.externalEventId,
      editScope,
    });
    dismiss();
  };

  // A recurring event needs the scope picked before anything is destroyed, so
  // that stands in for the plain confirmation rather than following it.
  if (event.isRecurring) {
    return (
      <RecurringEditScopeDialog
        open
        onClose={dismiss}
        onConfirm={runDelete}
        mode="delete"
        isPending={deleteMutation.isPending}
      />
    );
  }

  return (
    <Dialog open onOpenChange={(next) => !next && dismiss()}>
      <DialogContent className="max-w-sm">
        <div className="space-y-4">
          {/* `DialogTitle` and `DialogDescription` rather than a bare heading and
              paragraph: Radix wires them to the dialog's accessible name and
              description, and logs an error for every dialog opened without
              them. Those errors were the loudest thing in the console capture on
              BIG-8266, and none of them were the bug. */}
          <DialogTitle className="text-lg font-semibold">Delete event</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {isOrganizer
              ? `Are you sure you want to delete "${event.summary}"? This will cancel the event and notify all attendees.`
              : `Are you sure you want to remove "${event.summary}" from your calendar?`}
          </DialogDescription>
          <div className="flex items-center gap-2 justify-end">
            <Button variant="outline" data-statsig-name="event-delete-cancel" onClick={dismiss}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              data-statsig-name="event-delete-confirm"
              onClick={() => runDelete()}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
              Delete
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
